'use client';

import { useState, useEffect } from 'react';


interface WhatsAppFloatingButtonProps {
  phoneNumber: string;
  defaultMessage?: string;
  showAfterScroll?: number;
}

const WhatsAppFloatingButton = ({ 
  phoneNumber, 
  defaultMessage = 'Hello, I would like to inquire about your EPC services.',
  showAfterScroll = 300 
}: WhatsAppFloatingButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > showAfterScroll) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [showAfterScroll]);

  const handleWhatsAppClick = () => {
    const formattedPhone = phoneNumber.replace(/[^0-9]/g, '');
    const encodedMessage = encodeURIComponent(defaultMessage);
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={handleWhatsAppClick}
      className="fixed bottom-6 right-6 z-[90] w-14 h-14 bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-full shadow-card flex items-center justify-center transition-all duration-250 hover:scale-110 active:scale-95"
      aria-label="Contact us on WhatsApp"
      title="Chat with us on WhatsApp"
    >
      <svg
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M16 2C8.268 2 2 8.268 2 16C2 18.592 2.716 21.016 3.968 23.072L2.156 29.156L8.464 27.384C10.444 28.488 12.716 29.12 15.12 29.12C22.852 29.12 29.12 22.852 29.12 15.12C29.12 7.388 22.732 2 16 2ZM16 4C21.652 4 27.12 8.468 27.12 15.12C27.12 21.772 21.772 27.12 15.12 27.12C12.896 27.12 10.804 26.536 8.992 25.512L8.464 25.224L4.624 26.248L5.672 22.528L5.36 21.976C4.24 20.092 3.6 17.916 3.6 15.6C3.6 9.348 9.348 4 16 4ZM11.2 9.6C10.96 9.6 10.552 9.696 10.216 10.056C9.88 10.416 8.8 11.424 8.8 13.464C8.8 15.504 10.24 17.472 10.456 17.736C10.672 18 13.36 22.128 17.488 23.88C20.896 25.344 21.616 25.056 22.384 24.984C23.152 24.912 24.88 23.976 25.24 23.016C25.6 22.056 25.6 21.24 25.504 21.072C25.408 20.904 25.144 20.808 24.736 20.616C24.328 20.424 22.312 19.416 21.928 19.272C21.544 19.128 21.28 19.056 21.016 19.464C20.752 19.872 19.984 20.808 19.744 21.072C19.504 21.336 19.264 21.36 18.856 21.168C18.448 20.976 17.104 20.544 15.52 19.128C14.272 18.024 13.432 16.656 13.192 16.248C12.952 15.84 13.168 15.6 13.36 15.408C13.528 15.24 13.768 14.952 13.96 14.712C14.152 14.472 14.224 14.304 14.368 14.04C14.512 13.776 14.44 13.536 14.344 13.344C14.248 13.152 13.432 11.112 13.072 10.296C12.712 9.48 12.352 9.6 12.112 9.6C11.872 9.6 11.608 9.6 11.344 9.6C11.08 9.6 10.648 9.696 10.264 10.104C9.88 10.512 8.8 11.52 8.8 13.56C8.8 15.6 10.288 17.568 10.504 17.832C10.72 18.096 13.432 22.224 17.56 23.976C20.968 25.44 21.688 25.152 22.456 25.08C23.224 25.008 24.952 24.072 25.312 23.112C25.672 22.152 25.672 21.336 25.576 21.168C25.48 21 25.216 20.904 24.808 20.712C24.4 20.52 22.384 19.512 22 19.368C21.616 19.224 21.352 19.152 21.088 19.56C20.824 19.968 20.056 20.904 19.816 21.168C19.576 21.432 19.336 21.456 18.928 21.264C18.52 21.072 17.176 20.64 15.592 19.224C14.344 18.12 13.504 16.752 13.264 16.344C13.024 15.936 13.24 15.696 13.432 15.504C13.6 15.336 13.84 15.048 14.032 14.808C14.224 14.568 14.296 14.4 14.44 14.136C14.584 13.872 14.512 13.632 14.416 13.44C14.32 13.248 13.504 11.208 13.144 10.392C12.784 9.576 12.424 9.696 12.184 9.696C11.944 9.696 11.68 9.696 11.416 9.696L11.2 9.6Z"
          fill="currentColor"
        />
      </svg>
      <span className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full animate-pulse" />
    </button>
  );
};

export default WhatsAppFloatingButton;